// The "thanks" state (Trustpilot's post-submit page): "Thanks for your
// rating!", the company card joined to the review card by a grey bracket, the
// review card (banner, reviewer, stars, derived title, comment, meta chips,
// Edit), and the lower "What's next?" band with a green info card, a white
// care-team card and a centred dashboard pill. A link that already has a
// rating opens here directly.

import CheckRounded from "@mui/icons-material/CheckRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { makeStyles } from "@mui/styles";

import StarTiles from "~/shared/components/StarTiles.jsx";
import {
  RATING_COLORS,
  RATING_MOBILE_MEDIA_QUERY,
} from "~/shared/styles/ratingPalette";
import CompanyCard from "./CompanyCard.jsx";
import { PillLink } from "./ratingUi.jsx";
import {
  deriveReviewTitle,
  formatVisitDate,
  tagLabelsFor,
} from "./ratingUtils.js";

export const THANKS_HEADING = "Thanks for your rating!";
export const THANKS_BANNER = "Your rating has been sent to the Kick team.";
export const THANKS_BANNER_TAIL = "Thank you!";
export const VERIFIED_PATIENT = "Verified patient";
export const VERIFIED_VISIT_CHIP = "Verified visit";
export const NEXT_HEADING = "What's next?";
export const NEXT_GREEN_TITLE = "Your feedback goes to your care team";
export const NEXT_GREEN_BODY =
  "Every rating is read by the Kick team and helps us improve your treatment experience.";
export const NEXT_WHITE_TITLE = "Questions about your treatment?";
export const NEXT_WHITE_BODY =
  "Refills, dose changes and side-effect questions all go to your care team through the portal.";
export const MESSAGE_CARE_TEAM_LABEL = "Message the care team";
export const DASHBOARD_LABEL = "Go to your dashboard";

const CONNECTOR_OFFSET = 24;
const CONNECTOR_ARM = 14;
const CARD_GAP = 16;

const connectorStroke = `2px solid ${RATING_COLORS.connector}`;

const useStyles = makeStyles({
  // First thing under the dark header (the shell's topGap is 0 on this page),
  // so the heading carries its own breathing room.
  h1: {
    fontSize: "32px",
    fontWeight: 700,
    lineHeight: 1.25,
    margin: "48px 0 16px",
    color: RATING_COLORS.headingInk,
    [RATING_MOBILE_MEDIA_QUERY]: {
      fontSize: "28px",
      marginTop: "32px",
    },
  },
  companyCard: {
    position: "relative",
    marginBottom: `${CARD_GAP}px`,
    // Upper half of the bracket: from the card's middle down into the gap.
    "&::after": {
      content: '""',
      position: "absolute",
      left: `-${CONNECTOR_OFFSET}px`,
      width: `${CONNECTOR_ARM}px`,
      top: "50%",
      bottom: `-${CARD_GAP}px`,
      borderLeft: connectorStroke,
      borderTop: connectorStroke,
      borderRadius: "8px 0 0 0",
      pointerEvents: "none",
    },
    [RATING_MOBILE_MEDIA_QUERY]: {
      "&::after": {
        display: "none",
      },
    },
  },
  // The banner sits outside the bordered box, with square bottom corners.
  banner: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "14px 16px",
    backgroundColor: RATING_COLORS.infoBanner,
    borderRadius: "8px 8px 0 0",
    fontSize: "14px",
    lineHeight: "20px",
    color: RATING_COLORS.textPrimary,
    "& strong": {
      fontWeight: 600,
    },
    // Lower half of the bracket: down the banner and back in at its bottom.
    "&::before": {
      content: '""',
      position: "absolute",
      left: `-${CONNECTOR_OFFSET}px`,
      width: `${CONNECTOR_ARM}px`,
      top: 0,
      bottom: 0,
      borderLeft: connectorStroke,
      borderBottom: connectorStroke,
      borderRadius: "0 0 0 8px",
      pointerEvents: "none",
    },
    [RATING_MOBILE_MEDIA_QUERY]: {
      "&::before": {
        display: "none",
      },
    },
  },
  bannerIcon: {
    fontSize: "18px",
    flexShrink: 0,
  },
  body: {
    backgroundColor: "#FFFFFF",
    border: `1px solid ${RATING_COLORS.borderCard}`,
    borderRadius: "0 0 8px 8px",
    padding: "24px",
  },
  reviewer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
  },
  avatar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: RATING_COLORS.accentPaleBg,
    color: RATING_COLORS.accent,
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1,
  },
  reviewerName: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1.5,
    color: RATING_COLORS.textPrimary,
  },
  reviewerMeta: {
    fontSize: "14px",
    lineHeight: "20px",
    color: RATING_COLORS.textSecondary,
  },
  divider: {
    border: 0,
    borderTop: `1px solid ${RATING_COLORS.borderCard}`,
    margin: "0 0 16px",
  },
  stars: {
    lineHeight: 0,
    marginBottom: "16px",
  },
  title: {
    margin: "0 0 12px",
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.4,
    color: RATING_COLORS.textPrimary,
    overflowWrap: "anywhere",
  },
  comment: {
    margin: "0 0 16px",
    fontSize: "16px",
    lineHeight: 1.5,
    color: RATING_COLORS.textPrimary,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },
  metaChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "16px",
  },
  metaChip: {
    display: "inline-flex",
    alignItems: "center",
    height: "20px",
    padding: "0 8px",
    borderRadius: "4px",
    backgroundColor: RATING_COLORS.chipFill,
    fontSize: "12px",
    lineHeight: "20px",
    color: RATING_COLORS.textPrimary,
  },
  editButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 0",
    border: 0,
    background: "none",
    fontFamily: "inherit",
    fontSize: "16px",
    lineHeight: 1.5,
    color: RATING_COLORS.textSecondary,
    cursor: "pointer",
    borderRadius: "4px",
    "&:hover": {
      color: RATING_COLORS.textPrimary,
    },
    "&:focus-visible": {
      outline: `2px solid ${RATING_COLORS.accent}`,
      outlineOffset: "2px",
    },
  },
  editIcon: {
    fontSize: "18px",
  },

  // ---- lower band ----
  nextHeading: {
    fontSize: "24px",
    fontWeight: 700,
    lineHeight: 1.25,
    margin: "0 0 16px",
    color: RATING_COLORS.textPrimary,
  },
  greenCard: {
    position: "relative",
    overflow: "hidden",
    padding: "24px",
    borderRadius: "16px",
    backgroundColor: RATING_COLORS.celebrateCard,
    color: RATING_COLORS.celebrateInk,
    marginBottom: "16px",
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      borderRadius: "50%",
      backgroundColor: RATING_COLORS.celebrateDeco,
      pointerEvents: "none",
    },
    "&::before": {
      width: "240px",
      height: "240px",
      right: "-70px",
      top: "-110px",
    },
    "&::after": {
      width: "150px",
      height: "150px",
      right: "60px",
      bottom: "-80px",
    },
  },
  cardContent: {
    position: "relative",
    zIndex: 1,
  },
  cardTitle: {
    margin: "0 0 8px",
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.4,
    color: "inherit",
  },
  cardBody: {
    margin: 0,
    fontSize: "14px",
    lineHeight: "20px",
    color: "inherit",
  },
  // White "widget" tile bleeding out of the green card's bottom edge.
  greenTile: {
    display: "inline-flex",
    marginTop: "16px",
    marginBottom: "-36px",
    padding: "12px",
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    lineHeight: 0,
  },
  whiteCard: {
    padding: "24px",
    borderRadius: "16px",
    backgroundColor: "#FFFFFF",
    border: `1px solid ${RATING_COLORS.borderCard}`,
    color: RATING_COLORS.textPrimary,
  },
  whiteCardTitle: {
    color: RATING_COLORS.headingInk,
  },
  checkCircle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: RATING_COLORS.celebrateStar,
    color: RATING_COLORS.celebrateInk,
    marginBottom: "16px",
    "& svg": {
      fontSize: "22px",
    },
  },
  whiteCardAction: {
    marginTop: "16px",
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "48px",
    textAlign: "center",
  },
  footerLine: {
    margin: "0 0 16px",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1.5,
    color: RATING_COLORS.textPrimary,
  },
});

function firstNameOf(invitation) {
  return ((invitation && invitation.firstName) || "").trim();
}

export default function RatingThankYou({ rating, invitation, onEdit }) {
  const classes = useStyles();
  const firstName = firstNameOf(invitation);
  const initial = firstName ? firstName.charAt(0).toUpperCase() : "";
  const visitDate = formatVisitDate(invitation && invitation.visitDate);
  const title = deriveReviewTitle(rating.comment);
  const tagLabels = tagLabelsFor(
    rating.tags || [],
    (invitation && invitation.tags) || [],
  );

  return (
    <div>
      <h1 className={classes.h1}>{THANKS_HEADING}</h1>

      <CompanyCard variant="thanks" className={classes.companyCard} />

      <div>
        <div className={classes.banner}>
          <InfoOutlined className={classes.bannerIcon} aria-hidden="true" />
          <span>
            <strong>{THANKS_BANNER}</strong> {THANKS_BANNER_TAIL}
          </span>
        </div>
        <div className={classes.body}>
          <div className={classes.reviewer}>
            {initial ? (
              <span className={classes.avatar} aria-hidden="true">
                {initial}
              </span>
            ) : null}
            <div>
              {firstName ? (
                <div className={classes.reviewerName}>
                  <strong>{firstName}</strong>
                </div>
              ) : null}
              <div className={classes.reviewerMeta}>{VERIFIED_PATIENT}</div>
            </div>
          </div>
          <hr className={classes.divider} />
          <div className={classes.stars}>
            <StarTiles value={rating.stars} size={20} gap={2} readOnly />
          </div>
          {title ? <p className={classes.title}>{title}</p> : null}
          <p className={classes.comment}>{rating.comment}</p>
          <div className={classes.metaChips}>
            {visitDate ? (
              <span className={classes.metaChip}>{visitDate}</span>
            ) : null}
            <span className={classes.metaChip}>{VERIFIED_VISIT_CHIP}</span>
            {tagLabels.map((label) => (
              <span key={label} className={classes.metaChip}>
                {label}
              </span>
            ))}
          </div>
          <hr className={classes.divider} />
          <button type="button" className={classes.editButton} onClick={onEdit}>
            <EditOutlined className={classes.editIcon} aria-hidden="true" />
            Edit
          </button>
        </div>
      </div>
    </div>
  );
}

// Lower band content ("What's next?") — rendered by RatingShell's band slot so
// it can be full-bleed under the column.
export function RatingThankYouBand({ rating, invitation }) {
  const classes = useStyles();
  const firstName = firstNameOf(invitation);
  return (
    <div>
      <h2 className={classes.nextHeading}>{NEXT_HEADING}</h2>

      <div className={classes.greenCard}>
        <div className={classes.cardContent}>
          <h3 className={classes.cardTitle}>{NEXT_GREEN_TITLE}</h3>
          <p className={classes.cardBody}>{NEXT_GREEN_BODY}</p>
          <div className={classes.greenTile} aria-hidden="true">
            <StarTiles value={rating ? rating.stars : 0} size={40} readOnly />
          </div>
        </div>
      </div>

      <div className={classes.whiteCard}>
        <span className={classes.checkCircle} aria-hidden="true">
          <CheckRounded />
        </span>
        <h3 className={[classes.cardTitle, classes.whiteCardTitle].join(" ")}>
          {NEXT_WHITE_TITLE}
        </h3>
        <p className={classes.cardBody}>{NEXT_WHITE_BODY}</p>
        <div className={classes.whiteCardAction}>
          <PillLink variant="pale" href="/me/messages/care">
            {MESSAGE_CARE_TEAM_LABEL}
          </PillLink>
        </div>
      </div>

      <div className={classes.footer}>
        <p className={classes.footerLine}>
          {firstName ? `Thanks again, ${firstName}!` : "Thanks again!"}
        </p>
        <PillLink href="/me/portal">{DASHBOARD_LABEL}</PillLink>
      </div>
    </div>
  );
}
